"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Film, Mail, Pencil, X } from "lucide-react";

type Unread = {
  id: number;
  from_email: string;
  body: string;
  project_id: number | null;
  shot_id: number | null;
  take_id: number | null;
  shot_number: number | null;
  scene_number: number | null;
  take_no: number | null;
  in_point: number | null;
  out_point: number | null;
  image_url: string | null;
  created_at: string;
};

/**
 * Splash globale dei messaggi DIRETTI non letti: chi riceve un "Invia messaggio"
 * (dal context menu di un take) vede comparire il messaggio ovunque si trovi.
 * Poll /api/messages/inbox; alla chiusura marca letto. Va allo shot col take citato.
 */
export default function MessageSplash() {
  const router = useRouter();
  const [queue, setQueue] = useState<Unread[]>([]);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch("/api/messages/inbox")
        .then((r) => r.json())
        .then((d) => {
          if (alive) setQueue(Array.isArray(d?.unread) ? d.unread : []);
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const current = queue[0];
  if (!current) return null;

  const dismiss = (go?: boolean) => {
    fetch(`/api/messages/${current.id}/read`, { method: "POST" }).catch(() => {});
    setQueue((q) => q.slice(1));
    if (go && current.project_id && current.shot_id) {
      const q = new URLSearchParams();
      if (current.take_id) q.set("take", String(current.take_id));
      if (current.in_point != null) q.set("t", String(current.in_point));
      if (current.out_point != null) q.set("tout", String(current.out_point));
      const qs = q.toString();
      router.push(`/projects/${current.project_id}/shot/${current.shot_id}${qs ? `?${qs}` : ""}`);
    }
  };

  const who = current.from_email.split("@")[0];
  const shotRef =
    current.scene_number != null && current.shot_number != null
      ? `shot ${current.scene_number}.${current.shot_number}`
      : null;
  const takeRef = current.take_no != null ? `take ${String(current.take_no).padStart(2, "0")}` : null;
  const ctx = [takeRef, shotRef].filter(Boolean).join(" · ") || null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4" onClick={() => dismiss(false)}>
      <div
        className="w-full max-w-md rounded-2xl border border-accent/40 bg-panel p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/20 text-accent">
            <Mail size={18} />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">Messaggio da {who}</div>
            {ctx && <div className="text-[11px] text-ink-3">a proposito di {ctx}</div>}
          </div>
          {queue.length > 1 && (
            <span className="ml-auto rounded-full bg-panel-2 px-2 py-0.5 text-[10px] text-ink-3">
              +{queue.length - 1} altri
            </span>
          )}
        </div>
        <p className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-edge bg-field px-4 py-3 text-[14px] leading-relaxed text-ink">
          {current.body}
        </p>
        {current.image_url && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-accent">
            <Pencil size={12} /> Con un disegno sul frame — apri il take per vederlo
          </div>
        )}
        {current.shot_id && current.take_id && (
          <button
            onClick={() => dismiss(true)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/10 px-2.5 py-1 text-xs font-semibold text-accent hover:bg-accent/20"
          >
            <Film size={13} /> {takeRef ? `Apri il ${takeRef}` : "Apri il take"}{shotRef ? ` (${shotRef})` : ""}
          </button>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => dismiss(false)}
            className="inline-flex items-center gap-1 rounded-md border border-edge px-3 py-1.5 text-xs text-ink-2 hover:text-ink"
          >
            <X size={13} /> Chiudi
          </button>
          {current.shot_id && (
            <button
              onClick={() => dismiss(true)}
              className="rounded-md bg-accent px-3.5 py-1.5 text-xs font-semibold text-[#1b1b1f] hover:bg-accent-2"
            >
              {takeRef ? `Vai al ${takeRef}` : "Vai al take"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
